import { Body, Checkbox } from '@assembly-js/design-system';
import { useState } from 'react';

import {
  AppNotificationSetting,
  NotificationSurface,
} from 'src/constants/dataTypes/appsTypes';
import {
  NOTIFICATION_SURFACES,
  type NotificationSettingDraft,
} from 'src/features/app-notification-settings/notificationConfig';
import { ModalWrapper } from 'src/legacy/components/Modals';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { BaseTextField } from 'src/legacy/components/TextField';

const DEFAULT_NEW_SURFACES: NotificationSurface[] = ['product', 'email'];

interface NotificationSettingModalProps {
  // The setting being edited, or null when adding a new one.
  initialSetting: AppNotificationSetting | null;
  onClose: () => void;
  onSave: (draft: NotificationSettingDraft) => void;
}

// Modal that authors a single notification setting. It holds local draft state so closing with
// Cancel discards edits; the parent commits the draft to the Formik settings array on save.
export function NotificationSettingModal({
  initialSetting,
  onClose,
  onSave,
}: NotificationSettingModalProps) {
  const isEditing = initialSetting != null;
  const [label, setLabel] = useState(initialSetting?.label ?? '');
  const [surfaces, setSurfaces] = useState<NotificationSurface[]>(
    initialSetting?.surfaces ?? DEFAULT_NEW_SURFACES,
  );

  const toggleSurface = (surface: NotificationSurface, checked: boolean) => {
    setSurfaces((prev) =>
      checked
        ? Array.from(new Set([...prev, surface]))
        : prev.filter((value) => value !== surface),
    );
  };

  const canSave = label.trim().length > 0 && surfaces.length > 0;

  return (
    <ModalWrapper
      open
      width={520}
      title={
        isEditing ? 'Edit notification setting' : 'Add notification setting'
      }
      description="This becomes a row on every internal user's notifications page."
      successButtonLabel={isEditing ? 'Save setting' : 'Add setting'}
      cancelButtonLabel="Cancel"
      disableSuccessButton={!canSave}
      onClose={onClose}
      onSuccess={() => onSave({ label: label.trim(), surfaces })}
    >
      <div className="flex flex-col gap-5">
        <div>
          <BaseTypography fontType="13Medium">Label</BaseTypography>
          <Body size="sm" className="text-gray-small mb-2">
            What an internal user sees in the &ldquo;Notify me about&rdquo;
            column. Describe the event, not the app.
          </Body>
          <BaseTextField
            fullWidth
            autoFocus
            name="notificationSettingLabel"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. New task assigned"
            sizeVariant="medium"
            variant="outlined"
          />
        </div>
        <div>
          <BaseTypography fontType="13Medium">Surfaces</BaseTypography>
          <Body size="sm" className="text-gray-small mb-2">
            Channels this setting can be delivered on. Internal users only see
            toggles for the surfaces you enable.
          </Body>
          <div className="flex flex-col gap-2">
            {NOTIFICATION_SURFACES.map((surface) => (
              <div
                key={surface.id}
                className="border border-solid border-non-hover-border rounded-lg px-3 py-2.5"
              >
                <Checkbox
                  label={surface.label}
                  description={surface.description}
                  checked={surfaces.includes(surface.id)}
                  onChange={(e) => toggleSurface(surface.id, e.target.checked)}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </ModalWrapper>
  );
}
